import { isPromiseLike } from '@/lib/utils/type-guards';

type Awaitable<T> = PromiseLike<T> | Promise<T>;

const swallowRejection = (value: unknown): void => {
  if (isPromiseLike(value)) {
    Promise.resolve(value).catch(() => {
      // Intentionally ignore handler errors to keep fire-and-forget semantics.
    });
  }
};

/**
 * Converts a promise-returning callback into a void-returning handler so it can be safely
 * passed to React event props that expect synchronous callbacks.
 */
export function toVoidPromise<Arguments extends unknown[]>(
  callback: (...arguments_: Arguments) => Awaitable<void>
): (...arguments_: Arguments) => void {
  return (...arguments_: Arguments) => {
    swallowRejection(callback(...arguments_));
  };
}

/**
 * Creates a void-returning handler that invokes the provided callback and forwards any error to the supplied sink.
 * This allows consumers to surface failures without violating lint rules that enforce synchronous event handlers.
 */
export function toVoidPromiseWithErrorSink<Arguments extends unknown[]>(
  callback: (...arguments_: Arguments) => Awaitable<void>,
  onError: (error: unknown) => void
): (...arguments_: Arguments) => void {
  return (...arguments_: Arguments) => {
    const task = Promise.resolve(callback(...arguments_)).catch(onError);
    swallowRejection(task);
  };
}
